import { OrganizationList } from '@/components'
import { usePositionStorage } from '@/hooks'
import { safeRedirectPath } from '@/lib'
import { type AuthSsoCallback403, useAuthSelectOrganisation, useAuthSsoCallback } from '@jobilla/nexus-sdk'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthLayout, EmailVerificationForm, JobillaBrandLogo, logger } from '../ui'

interface OrgSelectionState {
	pendingAuthenticationToken: string
	organizations: Array<{ id: string; name: string }>
}

interface EmailVerificationState {
	email: string
	pendingAuthenticationToken: string
}

export const Route = createFileRoute('/callback')({
	component: CallbackComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		code: (search.code as string) ?? '',
	}),
})

function CallbackComponent() {
	const { t } = useTranslation('common')
	const { code } = Route.useSearch()
	const navigate = useNavigate()
	const [error, setError] = useState<string | null>(null)
	const [orgSelection, setOrgSelection] = useState<OrgSelectionState | null>(null)
	const [verification, setVerification] = useState<EmailVerificationState | null>(null)
	const { getRedirect, clearRedirect, getInviteToken, clearInviteToken } = usePositionStorage()
	const ssoCallback = useAuthSsoCallback()
	const calledRef = useRef(false)

	function redirectAfterAuth(defaultPath?: string) {
		const pendingRedirect = getRedirect()
		clearRedirect()
		window.location.href = safeRedirectPath(pendingRedirect, defaultPath ?? '/company')
	}

	useEffect(() => {
		if (!code) {
			void navigate({ to: '/auth' })
			return
		}

		if (calledRef.current) {
			return
		}
		calledRef.current = true

		// If there's a pending invite token from the /invite page, pass it to the callback
		// so WorkOS can accept the invitation as part of authentication.
		const invitationToken = getInviteToken()
		if (invitationToken) {
			clearInviteToken()
		}

		ssoCallback.mutate({ data: { code, invitationToken: invitationToken ?? undefined } }, {
			onSuccess: () => {
				if (invitationToken) {
					// Invitation was accepted by WorkOS during the SSO flow.
					// Clear the saved redirect (which points to /invite) and go to /company.
					clearRedirect()
					window.location.href = '/company'
					return
				}
				redirectAfterAuth()
			},
			onError: (err) => {
				if (isAxiosError<AuthSsoCallback403>(err) && err.response?.status === 403) {
					const data = err.response.data
					if (
						data.code === 'organization_selection_required'
						&& data.pending_authentication_token
						&& data.organizations
					) {
						setOrgSelection({
							pendingAuthenticationToken: data.pending_authentication_token,
							organizations: data.organizations,
						})
						return
					}
					if (
						data.code === 'email_verification_required' && data.email && data.pending_authentication_token
					) {
						logger.warn('SSO callback requires email verification', {
							category: 'auth',
							data: { code: data.code },
						})
						setVerification({
							email: data.email,
							pendingAuthenticationToken: data.pending_authentication_token,
						})
						return
					}
					logger.warn('SSO callback returned unhandled auth challenge', {
						category: 'auth',
						data: { code: data.code, status: 403 },
					})
					setError(t('auth.callback.errorDefault', 'Authentication failed. Please try again.'))
					return
				}
				if (
					isAxiosError(err) && err.response?.status && err.response.status >= 400 && err.response.status < 500
				) {
					logger.warn('SSO callback client error', {
						category: 'auth',
						data: { status: err.response.status, action: 'sso-callback' },
					})
				} else {
					logger.error(err, { category: 'auth', data: { action: 'sso-callback' } })
				}
				setError(t('auth.callback.errorDefault', 'Authentication failed. Please try again.'))
			},
		})
	}, [code]) // eslint-disable-line react-hooks/exhaustive-deps

	const selectOrganisationMutation = useAuthSelectOrganisation({}, {
		withCredentials: true,
	})

	if (orgSelection) {
		return (
			<AuthLayout>
				<OrganizationList
					organizations={orgSelection.organizations}
					onSelect={(org) => {
						void (async () => {
							try {
								const data = await selectOrganisationMutation.mutateAsync({
									data: {
										organisationId: org.id,
										pendingAuthenticationToken: orgSelection.pendingAuthenticationToken,
									},
								})
								const slug = data.organisationSlug
								redirectAfterAuth(slug ? `/company/${slug}` : undefined)
							} catch {
								setOrgSelection(null)
								setError(t('auth.callback.errorDefault', 'Authentication failed. Please try again.'))
							}
						})()
					}}
				/>
			</AuthLayout>
		)
	}

	if (verification) {
		return (
			<AuthLayout>
				<EmailVerificationForm
					email={verification.email}
					pendingAuthenticationToken={verification.pendingAuthenticationToken}
				/>
			</AuthLayout>
		)
	}

	if (error) {
		return (
			<AuthLayout>
				<div className="w-full max-w-lg mx-auto p-8">
					<JobillaBrandLogo />

					<h1 className="mt-8 text-3xl font-semibold text-gray-800 tracking-[0.01em]">
						{t('auth.callback.errorTitle', 'Authentication failed')}
					</h1>
					<p className="mt-3 text-base text-gray-500">
						{error}
					</p>

					<a
						href="/auth"
						className="
							mt-8 w-full py-3 px-4 rounded-full font-medium text-white text-base
							bg-gradient-to-r from-[#225DFC] to-[#9726FA]
							hover:brightness-130 hover:shadow-xl hover:translate-y-[-1px]
							focus:outline-none focus:ring-2 focus:ring-[#6366F1]/50 focus:ring-offset-2
							shadow-lg shadow-[#225DFC]/30
							transition-all duration-400
							flex items-center justify-center
						"
					>
						{t('auth.callback.backToSignIn', 'Back to sign in')}
					</a>
				</div>
			</AuthLayout>
		)
	}

	return (
		<AuthLayout>
			<div className="w-full max-w-lg mx-auto p-8 text-center">
				<JobillaBrandLogo />
				<p className="mt-8 text-gray-400 animate-pulse">
					{t('auth.callback.authenticating', 'Authenticating...')}
				</p>
			</div>
		</AuthLayout>
	)
}
